import { useCallback, useEffect, useState } from 'react';
import AddViewPopover from '../../addView/components/AddViewPopover';
import { PinnedViews } from './PinnedViews';
import { Box } from 'foundations-components/transitional/components/Box';
import { AddViewButton } from './AddViewButton';
import emptyFunction from 'react-utils/emptyFunction';
import { DefaultPinnedViewsBarCreateViewModal } from './DefaultPinnedViewsBarCreateViewModal';
import { ViewName } from '../../singleView/components/ViewName';
import { DefaultPinnedViewsBarAllViewsLink } from './DefaultPinnedViewsBarAllViewsLinks';
import { ViewsManagementProvider } from '../../providers/ViewsManagementProvider';
import { AddViewPopoverFooter } from '../../addView/components/AddViewPopoverFooter';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BottomBorder = ({
  tabStyle
}) => /*#__PURE__*/_jsx(Flex, {
  borderBlockEndColor: tabStyle === 'modern' ? 'border-core-subtle' : 'border-core-default',
  borderBlockEndWidth: "100",
  borderBlockEndStyle: "solid"
});
const devNullLogger = {
  log: emptyFunction
};
/**
 * PinnedViewsBar gives you the "entire" view tabs UX in one easy to use simple component.
 *
 * ----------------------------------------------------------------------------
 * If you are getting started, this is an example of the easiest implemtnation
 * ex:
 * <PinnedViewsBar
 *    objectTypeId={CONTACT_TYPE_ID}
 *    namespace={VIEWS_NAMESPACE_FORMS}
 *    currentViewId={currentViewIdFromUrl}
 *    onCurrentViewIdChange={setViewIdInUrl}
 *    returnLink={urlOfCurrentLocation}
 * />
 *
 * ----------------------------------------------------------------------------
 * If you are trying to improve your apps performance, investiagte 'onShouldPrefetchViews'.
 * `onShouldPrefetchViews` will be called with an array of viewIds anytime the view tabs change. This includes first load, and any mutation by the user such as adding or removing a tab.
 * You can use this within your app to prefetch certain views (aka - cache warming).
 * Ex:
 * "A user opens my app that uses PinnedViewsBar, and every time they click on a view tab, the app has to wait to fetch that view"
 * "I expect the user will most likely click on a different tab, it would be great if we could fetch those views before they actually click on them, so when they do, they dont have to be shown a loading spinner"
 *
 * <PinnedViewsBar
 *    ...
 *    onShouldPrefetchViews={({viewIds}) => viewIds.forEach(viewId => fetchView(viewId).then(putInAppCache))}
 * />
 *
 * ----------------------------------------------------------------------------
 * If you are trying to insert a "badge" within view tabs to contain extra info, such as a "*" for unsaved, or "##" for unread messages, or "🔴" for "needs attention"
 *
 * You can accomplish that with ViewNameImplementation.
 * By default <PinnedViewsBar /> will use the <ViewName /> component as <ViewNameImplementation />. Internally, <ViewName /> will take an objectTypeId+namespace+viewId, to lookup/fetch a view object, and then render view.name.
 * You can provide a custom <ViewNameImplementation /> (as a prop to <PinnedViewsBar />), that allows you to inject custom content. You can still even use <ViewName /> in your own ViewNameImplementation!
 *
 * Ex:
 *
 * const CustomViewNameImplementation = (props: {viewId, objectTypeId, namespaceName}) => {
 *  const {data, loading, error} = useFetchUnreadCountFromBusinessLogicBackend({viewId, objectTypeId, namespaceName});
 *
 *  return <>
 *    <ViewName {...props}/> //you dont have to worry about how to render the actual view name, leave that to us! Less code for you, easier for us to maintain.
 *    {data && data.unreadCount && <UIBadge>{data && data.unreadCount}</UIBadge>}
 *  </>
 * }
 * <PinnedViewsBar
 *    ...
 *    ViewNameImplementation={CustomViewNameImplementation}
 * />
 *
 * ----------------------------------------------------------------------------
 *  If you are trying to customize the "All Views" link, you are able to do that too!
 *
 * Ex:
 *
 * const CustomAllViewsLink = (props: {objectTypeId, namespace}) => {
 *  return <a href="https://hubspot.com">All Views</a>
 * }
 * <PinnedViewsBar
 *    ...
 *    AllViewsLinkImplementation={CustomAllViewsLink}
 * />
 * ----------------------------------------------------------------------------
 */
export const PinnedViewsBar = ({
  objectTypeId,
  namespace,
  currentViewId,
  logger = devNullLogger,
  onShouldPrefetchViews = emptyFunction,
  onCurrentViewIdChange,
  CreateViewModalImplementation = DefaultPinnedViewsBarCreateViewModal,
  ViewNameImplementation = ViewName,
  AllViewsLinkImplementation = DefaultPinnedViewsBarAllViewsLink,
  tabStyle = 'legacy'
}) => {
  const [numberOfPinnedViews, setNumberOfPinnedViews] = useState(undefined);
  const [isCreateViewModalOpen, setIsCreateViewModalOpen] = useState(false);
  useEffect(() => {
    if (isCreateViewModalOpen) {
      logger.log({
        key: 'openCreateNewViewModal',
        metadata: {
          openTabs: numberOfPinnedViews || 0
        }
      });
    }
  }, [isCreateViewModalOpen, logger, numberOfPinnedViews]);
  const handleCloseViewTab = useCallback(viewId => {
    logger.log({
      key: 'closeTab',
      metadata: {
        openTabs: numberOfPinnedViews ? numberOfPinnedViews - 1 : 0,
        viewId
      }
    });
  }, [logger, numberOfPinnedViews]);
  const handleReorderViewTabs = useCallback(() => {
    logger.log({
      key: 'reorderTabs',
      metadata: {
        openTabs: numberOfPinnedViews || 0
      }
    });
  }, [logger, numberOfPinnedViews]);
  return /*#__PURE__*/_jsxs(ViewsManagementProvider, {
    children: [isCreateViewModalOpen && /*#__PURE__*/_jsx(CreateViewModalImplementation, {
      objectTypeId: objectTypeId,
      namespace: namespace,
      onClose: () => setIsCreateViewModalOpen(false),
      onCreate: viewId => {
        onCurrentViewIdChange(viewId);
        setIsCreateViewModalOpen(false);
      }
    }), /*#__PURE__*/_jsxs(Flex, {
      marginBlockEnd: "-25",
      minInlineSize: "0",
      "data-test-id": "view-tabs",
      children: [/*#__PURE__*/_jsx(Box, {
        flexGrow: 0,
        flexShrink: 1,
        flexBasis: "auto",
        overflow: "hidden",
        "data-selenium-test": "onboarding-tour-crm-datasets-index-page-views-redesign",
        children: /*#__PURE__*/_jsx(PinnedViews, {
          objectTypeId: objectTypeId,
          namespace: namespace,
          currentViewId: currentViewId,
          onCloseViewTab: handleCloseViewTab,
          onCurrentViewIdChange: onCurrentViewIdChange,
          onPrefetchViews: ({
            viewIds
          }) => {
            setNumberOfPinnedViews(viewIds.length);
            onShouldPrefetchViews({
              viewIds
            });
          },
          onReorderViewTabs: handleReorderViewTabs,
          ViewNameImplementation: ViewNameImplementation,
          tabStyle: tabStyle
        })
      }), /*#__PURE__*/_jsxs(Box, {
        grow: 0,
        shrink: 0,
        basis: "auto",
        children: [/*#__PURE__*/_jsx(AddViewPopover, {
          namespace: namespace,
          objectTypeId: objectTypeId,
          footer: /*#__PURE__*/_jsx(AddViewPopoverFooter, {
            onClick: () => {
              document.body.click(); //we need to collapse the add view popover - clicking on the body before opening the create view modal seems like a decent way to do it
              setIsCreateViewModalOpen(true);
            }
          }),
          onChange: view => {
            onCurrentViewIdChange(view.id);
          },
          children: /*#__PURE__*/_jsx(AddViewButton, {
            numberOfPinnedViews: numberOfPinnedViews
          })
        }), /*#__PURE__*/_jsx(AllViewsLinkImplementation, {
          objectTypeId: objectTypeId,
          namespace: namespace
        })]
      })]
    }), /*#__PURE__*/_jsx(BottomBorder, {
      tabStyle: tabStyle
    })]
  });
};